import Environment from './Environment';
import Features from './Features';

// Kill switch; read before the experiment, so off means no exposures.
const UNIFIED_CHECKOUT_GATE = '2026-enable_unified_checkout';

/* Splits gated shoppers, bucketed on stableID. Names must match the console
   exactly; a typo silently serves the fallback. */
const UNIFIED_CHECKOUT_EXPERIMENT = '2026_consumer_unified_checkout';
const UNIFIED_CHECKOUT_PARAM = 'showsUnifiedCheckout';

/* Which checkout a shopper gets, shared so every caller agrees. Returns
   fallbacks until Statsig is ready; see whenVariantResolved. */
const showsUnifiedCheckout = () => {
  if (Environment.store !== 'bump') {
    return false;
  }

  // Gate first: reading the experiment logs the exposure.
  if (!Features.checkGate(UNIFIED_CHECKOUT_GATE)) {
    return false;
  }

  // Production only, so QA isn't bucketed into control or counted in Pulse.
  if (Environment.getEnv !== 'PRODUCTION') {
    return true;
  }

  /* Falls back to true so the gate alone decides when no experiment runs.
     The console's default value must be true too, or shoppers outside the
     allocation drop to the old checkout once the experiment starts. */
  return Features.getExperiment(UNIFIED_CHECKOUT_EXPERIMENT).get(
    UNIFIED_CHECKOUT_PARAM,
    true,
  );
};

// Kept verbatim (`!== 'bitsy'`) so the gate-off path is unchanged.
const showsLegacy2024Checkout = () =>
  Features.featureIsEnabled('2024-enable_new2024checkout') &&
  Environment.store !== 'bitsy';

// values_updated also fires for these, so an event isn't an answer.
const STATSIG_PENDING = ['Uninitialized', 'Loading'];

// 'Error' counts as resolved, so a failed init falls back instead of hanging.
const variantIsResolved = () =>
  Environment.store !== 'bump' ||
  !STATSIG_PENDING.includes(Features.getClient().loadingStatus);

// Calls back now and on every Statsig transition; returns an unsubscribe.
const subscribeToVariant = (fn) => {
  // Per subscription; module state would leak between tests.
  let answered = false;

  const emit = () => {
    const resolved = variantIsResolved();
    // Login briefly reports 'Loading'; don't flip a resolved caller back.
    if (answered && !resolved) return;
    answered = resolved;
    fn({ showsUnifiedCheckout: showsUnifiedCheckout(), resolved });
  };

  Features.getClient().on('values_updated', emit);
  emit();
  return () => Features.getClient().off('values_updated', emit);
};

// Awaitable; settles on timeout too, so a dead Statsig can't hang checkout.
const whenVariantResolved = (timeoutMs = 1500) =>
  new Promise((resolve) => {
    if (variantIsResolved()) {
      resolve(true);
      return;
    }

    let unsubscribe = null;
    let timer = null;
    let settled = false;

    const finish = (didResolve) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(didResolve);
    };

    timer = setTimeout(() => finish(false), timeoutMs);
    unsubscribe = subscribeToVariant(({ resolved }) => {
      if (resolved) finish(true);
    });
    // subscribeToVariant emits synchronously, before unsubscribe is assigned.
    if (settled) unsubscribe();
  });

export default {
  showsUnifiedCheckout,
  showsLegacy2024Checkout,
  variantIsResolved,
  subscribeToVariant,
  whenVariantResolved,
  UNIFIED_CHECKOUT_GATE,
  UNIFIED_CHECKOUT_EXPERIMENT,
};
